import React, { useState } from 'react';
import { AlertCircleIcon, ArrowLeftIcon, FileIcon, UploadIcon, XIcon } from 'lucide-react';
export function UploadCreditNote({
  vendor,
  onSuccess
}) {
  const [creditNoteData, setCreditNoteData] = useState({
    originalInvoiceNumber: '',
    creditNoteNumber: '',
    creditNoteDate: '',
    amount: '',
    reason: '',
    description: '',
    items: [{
      description: '',
      quantity: 1,
      unitPrice: 0
    }]
  });
  const [fileSelected, setFileSelected] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    setCreditNoteData({
      ...creditNoteData,
      [name]: value
    });
    // Clear error when field is updated
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...creditNoteData.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };
    // Recalculate totals
    if (field === 'quantity' || field === 'unitPrice') {
      const quantity = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(updatedItems[index].quantity) || 0;
      const unitPrice = field === 'unitPrice' ? parseFloat(value) || 0 : parseFloat(updatedItems[index].unitPrice) || 0;
      updatedItems[index].total = quantity * unitPrice;
    }
    setCreditNoteData({
      ...creditNoteData,
      items: updatedItems
    });
    // Update total amount
    const total = updatedItems.reduce((sum, item) => sum + (item.total || 0), 0);
    setCreditNoteData(prev => ({
      ...prev,
      amount: total.toFixed(2)
    }));
  };
  const addItem = () => {
    setCreditNoteData({
      ...creditNoteData,
      items: [...creditNoteData.items, {
        description: '',
        quantity: 1,
        unitPrice: 0,
        total: 0
      }]
    });
  };
  const removeItem = index => {
    if (creditNoteData.items.length === 1) return;
    const updatedItems = creditNoteData.items.filter((_, i) => i !== index);
    setCreditNoteData({
      ...creditNoteData,
      items: updatedItems
    });
    // Update total amount
    const total = updatedItems.reduce((sum, item) => sum + (item.total || 0), 0);
    setCreditNoteData(prev => ({
      ...prev,
      amount: total.toFixed(2)
    }));
  };
  const handleFileChange = e => {
    if (e.target.files && e.target.files[0]) {
      setFileSelected(e.target.files[0]);
      if (errors.file) {
        setErrors({
          ...errors,
          file: null
        });
      }
    }
  };
  const validateForm = () => {
    const newErrors = {};
    if (!creditNoteData.creditNoteNumber) newErrors.creditNoteNumber = 'Credit note number is required';
    if (!creditNoteData.creditNoteDate) newErrors.creditNoteDate = 'Credit note date is required';
    if (!creditNoteData.amount) newErrors.amount = 'Amount is required';
    if (!creditNoteData.reason) newErrors.reason = 'Reason is required';
    if (!fileSelected) newErrors.file = 'Please attach a credit note file';
    // Validate line items
    const itemErrors = [];
    creditNoteData.items.forEach((item, index) => {
      const itemError = {};
      if (!item.description) itemError.description = 'Description is required';
      if (!item.quantity || item.quantity <= 0) itemError.quantity = 'Valid quantity is required';
      if (!item.unitPrice || item.unitPrice <= 0) itemError.unitPrice = 'Valid price is required';
      if (Object.keys(itemError).length > 0) {
        itemErrors[index] = itemError;
      }
    });
    if (itemErrors.length > 0) {
      newErrors.items = itemErrors;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = e => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      onSuccess();
    }, 1500);
  };
  return <div data-id="element-827">
      <div className="mb-6" data-id="element-828">
        <button onClick={() => onSuccess()} className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-900" data-id="element-829">
          <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-830" />
          Back to Invoices
        </button>
      </div>
      <div className="mb-8" data-id="element-831">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-832">Upload Credit Note</h1>
        <p className="mt-1 text-sm text-gray-500" data-id="element-833">
          Fill in the details below and attach your credit note document
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-8" data-id="element-834">
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-835">
          <div className="px-4 py-5 sm:px-6" data-id="element-836">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-837">
              Credit Note Information
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-838">
              Please enter the basic credit note details
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:px-6" data-id="element-839">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-840">
              <div className="sm:col-span-3" data-id="element-841">
                <label htmlFor="originalInvoiceNumber" className="block text-sm font-medium text-gray-700" data-id="element-842">
                  Original Invoice Number
                </label>
                <div className="mt-1" data-id="element-843">
                  <input type="text" name="originalInvoiceNumber" id="originalInvoiceNumber" value={creditNoteData.originalInvoiceNumber} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-844" />
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-845">
                <label htmlFor="creditNoteNumber" className="block text-sm font-medium text-gray-700" data-id="element-846">
                  Credit Note Number *
                </label>
                <div className="mt-1" data-id="element-847">
                  <input type="text" name="creditNoteNumber" id="creditNoteNumber" value={creditNoteData.creditNoteNumber} onChange={handleChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.creditNoteNumber ? 'border-red-300' : ''}`} data-id="element-848" />
                  {errors.creditNoteNumber && <p className="mt-1 text-sm text-red-600" data-id="element-849">
                      {errors.creditNoteNumber}
                    </p>}
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-850">
                <label htmlFor="creditNoteDate" className="block text-sm font-medium text-gray-700" data-id="element-851">
                  Credit Note Date *
                </label>
                <div className="mt-1" data-id="element-852">
                  <input type="date" name="creditNoteDate" id="creditNoteDate" value={creditNoteData.creditNoteDate} onChange={handleChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.creditNoteDate ? 'border-red-300' : ''}`} data-id="element-853" />
                  {errors.creditNoteDate && <p className="mt-1 text-sm text-red-600" data-id="element-854">
                      {errors.creditNoteDate}
                    </p>}
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-855">
                <label htmlFor="reason" className="block text-sm font-medium text-gray-700" data-id="element-856">
                  Reason for Credit Note *
                </label>
                <div className="mt-1" data-id="element-857">
                  <select id="reason" name="reason" value={creditNoteData.reason} onChange={handleChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.reason ? 'border-red-300' : ''}`} data-id="element-858">
                    <option value="" data-id="element-859">Select a reason</option>
                    <option value="goods_returned" data-id="element-860">Goods Returned</option>
                    <option value="pricing_error" data-id="element-861">Pricing Error</option>
                    <option value="damaged_goods" data-id="element-862">Damaged Goods</option>
                    <option value="duplicate_invoice" data-id="element-863">Duplicate Invoice</option>
                    <option value="order_cancelled" data-id="element-864">Order Cancelled</option>
                    <option value="other" data-id="element-865">Other</option>
                  </select>
                  {errors.reason && <p className="mt-1 text-sm text-red-600" data-id="element-866">{errors.reason}</p>}
                </div>
              </div>
              <div className="sm:col-span-6" data-id="element-867">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-868">
                  Description
                </label>
                <div className="mt-1" data-id="element-869">
                  <textarea id="description" name="description" rows={3} value={creditNoteData.description} onChange={handleChange} placeholder="Additional details about this credit note" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border border-gray-300 rounded-md" data-id="element-870" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-871">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-872">
            <div data-id="element-873">
              <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-874">
                Line Items
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-875">
                Add items to be credited
              </p>
            </div>
            <button type="button" onClick={addItem} className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-876">
              Add Item
            </button>
          </div>
          <div className="border-t border-gray-200" data-id="element-877">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-878">
              <thead className="bg-gray-50" data-id="element-879">
                <tr data-id="element-880">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-881">
                    Description
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-882">
                    Quantity
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-883">
                    Unit Price
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-884">
                    Total
                  </th>
                  <th scope="col" className="relative px-6 py-3" data-id="element-885">
                    <span className="sr-only" data-id="element-886">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-887">
                {creditNoteData.items.map((item, index) => <tr key={index} data-id="element-888">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-889">
                      <input type="text" value={item.description} onChange={e => handleItemChange(index, 'description', e.target.value)} className={`block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${errors.items?.[index]?.description ? 'border-red-300' : ''}`} placeholder="Item description" data-id="element-890" />
                      {errors.items?.[index]?.description && <p className="mt-1 text-xs text-red-600" data-id="element-891">
                          {errors.items[index].description}
                        </p>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-892">
                      <input type="number" min="1" value={item.quantity} onChange={e => handleItemChange(index, 'quantity', e.target.value)} className={`block w-full shadow-sm sm:text-sm border-gray-300 rounded-md text-right ${errors.items?.[index]?.quantity ? 'border-red-300' : ''}`} data-id="element-893" />
                      {errors.items?.[index]?.quantity && <p className="mt-1 text-xs text-red-600" data-id="element-894">
                          {errors.items[index].quantity}
                        </p>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-895">
                      <div className="relative rounded-md shadow-sm" data-id="element-896">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-897">
                          <span className="text-gray-500 sm:text-sm" data-id="element-898">$</span>
                        </div>
                        <input type="number" min="0" step="0.01" value={item.unitPrice} onChange={e => handleItemChange(index, 'unitPrice', e.target.value)} className={`block w-full pl-7 pr-3 shadow-sm sm:text-sm border-gray-300 rounded-md text-right ${errors.items?.[index]?.unitPrice ? 'border-red-300' : ''}`} data-id="element-899" />
                        {errors.items?.[index]?.unitPrice && <p className="mt-1 text-xs text-red-600" data-id="element-900">
                            {errors.items[index].unitPrice}
                          </p>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right" data-id="element-901">
                      $
                      {((item.quantity || 0) * (item.unitPrice || 0)).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-902">
                      <button type="button" onClick={() => removeItem(index)} disabled={creditNoteData.items.length === 1} className={`text-red-600 hover:text-red-900 ${creditNoteData.items.length === 1 ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-903">
                        <XIcon className="h-5 w-5" data-id="element-904" />
                      </button>
                    </td>
                  </tr>)}
                <tr className="bg-gray-50" data-id="element-905">
                  <td colSpan={3} className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right" data-id="element-906">
                    Total Credit Amount
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right" data-id="element-907">
                    ${creditNoteData.amount || '0.00'}
                  </td>
                  <td data-id="element-908"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-909">
          <div className="px-4 py-5 sm:px-6" data-id="element-910">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-911">
              Credit Note Document
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-912">
              Attach your credit note file (PDF, JPG, PNG)
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:px-6" data-id="element-913">
            <div className="space-y-6" data-id="element-914">
              <div data-id="element-915">
                <label className="block text-sm font-medium text-gray-700" data-id="element-916">
                  Upload Credit Note *
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md" data-id="element-917">
                  <div className="space-y-1 text-center" data-id="element-918">
                    {fileSelected ? <div className="flex items-center justify-center" data-id="element-919">
                        <FileIcon className="h-10 w-10 text-indigo-500" data-id="element-920" />
                        <span className="ml-2 text-sm text-gray-600" data-id="element-921">
                          {fileSelected.name}
                        </span>
                        <button type="button" onClick={() => setFileSelected(null)} className="ml-2 text-red-600 hover:text-red-900" data-id="element-922">
                          <XIcon className="h-4 w-4" data-id="element-923" />
                        </button>
                      </div> : <>
                        <UploadIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-924" />
                        <div className="flex text-sm text-gray-600" data-id="element-925">
                          <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500" data-id="element-926">
                            <span data-id="element-927">Upload a file</span>
                            <input id="file-upload" name="file-upload" type="file" className="sr-only" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} data-id="element-928" />
                          </label>
                          <p className="pl-1" data-id="element-929">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500" data-id="element-930">
                          PDF, JPG, PNG up to 10MB
                        </p>
                      </>}
                  </div>
                </div>
                {errors.file && <p className="mt-1 text-sm text-red-600" data-id="element-931">{errors.file}</p>}
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end" data-id="element-932">
          <button type="button" onClick={() => onSuccess()} className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 mr-3" data-id="element-933">
            Cancel
          </button>
          <button type="submit" disabled={isSubmitting} className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-934">
            {isSubmitting ? 'Submitting...' : 'Submit Credit Note'}
          </button>
        </div>
        {Object.keys(errors).length > 0 && !errors.items && !errors.file && <div className="rounded-md bg-red-50 p-4" data-id="element-935">
            <div className="flex" data-id="element-936">
              <div className="flex-shrink-0" data-id="element-937">
                <AlertCircleIcon className="h-5 w-5 text-red-400" data-id="element-938" />
              </div>
              <div className="ml-3" data-id="element-939">
                <h3 className="text-sm font-medium text-red-800" data-id="element-940">
                  Please correct the errors above
                </h3>
              </div>
            </div>
          </div>}
      </form>
    </div>;
}